import { Box, Paper, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";

const InfoCardSkeleton = ({ showIcon = true }: { showIcon?: boolean }) => (
  <Paper
    sx={{
      p: 1,
      boxShadow: "none",
      border: "1px solid #0000001F",
      width: "194px",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
    }}
  >
    <Skeleton variant="text" width={90} height={14} />
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Skeleton variant="text" width={110} height={16} />
      {showIcon && <Skeleton variant="circular" width={22} height={22} />}
    </Box>
  </Paper>
);

const LastRunsCardSkeleton = () => (
  <Paper
    sx={{
      px: 1,
      py: 0.5,
      boxShadow: "none",
      border: "1px solid #0000001F",
      width: "320px",
      display: "flex",
      flexDirection: "column",
      gap: 1.2,
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box display="flex" gap={0.5} alignItems="center">
        <Skeleton variant="text" width={60} height={14} />
        <Skeleton variant="circular" width={16} height={16} />
      </Box>
      <Skeleton variant="rounded" width={110} height={30} />
    </Box>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Skeleton variant="text" width={160} height={16} />
      <Skeleton
        variant="rounded"
        width={70}
        height={26}
        sx={{ borderRadius: "16px" }}
      />
    </Box>
  </Paper>
);

const TableRowSkeleton = ({ cols }: { cols: number[] }) => (
  <Box
    display="grid"
    gridTemplateColumns={`repeat(${cols.length}, 1fr)`}
    gap={1}
    sx={{ py: 1.2, borderBottom: "1px solid #f0f0f0" }}
  >
    {cols.map((w, i) => (
      <Skeleton key={i} variant="text" width={`${w}%`} height={14} />
    ))}
  </Box>
);

const BrandConfigOverviewSkeleton = () => {
  const lastRecordsColWidths = [85, 90, 60, 95, 50, 50, 50, 70, 80, 60];
  const advisorColWidths = [60, 75, 40, 70];

  return (
    <>
      <Paper>
        {/* Header */}
        <Grid container>
          <Grid size={3} p={2}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Skeleton variant="text" width={260} height={32} />
              <Skeleton variant="text" width={160} height={16} />
            </Box>
          </Grid>

          <Grid
            size={9}
            pt={1}
            pb={2}
            px={2}
            display="flex"
            gap={1}
            justifyContent="flex-end"
            flexWrap="wrap"
          >
            <InfoCardSkeleton showIcon />
            <InfoCardSkeleton showIcon={false} />
            <InfoCardSkeleton showIcon />
            <LastRunsCardSkeleton />
          </Grid>
        </Grid>

        {/* Tabs Skeleton */}
        <Box
          display="flex"
          gap={0}
          sx={{ borderBottom: "1px solid #e0e0e0", px: 2 }}
        >
          {[160, 170, 190].map((w, i) => (
            <Box
              key={i}
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.8,
              }}
            >
              <Skeleton variant="text" width={w} height={14} />
            </Box>
          ))}
        </Box>

        {/* Last 30 Records Section */}
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width={140} height={22} sx={{ mb: 1.5 }} />

          {/* Table Header */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(10, 1fr)"
            gap={1}
            sx={{ pb: 1, borderBottom: "1px solid #e0e0e0", mb: 1 }}
          >
            {[
              "ID",
              "Activity Date",
              "Brand ID",
              "User Unique ID",
              "Regs",
              "FTD",
              "QFTD",
              "Net Revenue",
              "Revshare Income",
              "CPA Income",
            ].map((_, i) => (
              <Skeleton key={i} variant="text" height={14} />
            ))}
          </Box>

          {/* Table Rows */}
          {[85, 90, 80, 95, 88].map((firstCol, i) => (
            <TableRowSkeleton
              key={i}
              cols={lastRecordsColWidths.map((w, j) =>
                j === 0 ? firstCol : w,
              )}
            />
          ))}

          {/* Pagination */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pt: 1.5 }}
          >
            <Box display="flex" gap={1} alignItems="center">
              <Skeleton variant="rounded" width={60} height={30} />
              <Skeleton variant="text" width={60} height={14} />
            </Box>
            <Box display="flex" gap={0.5} alignItems="center">
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
              {[1, 2, 3, 4, 5].map((n) => (
                <Skeleton key={n} variant="circular" width={28} height={28} />
              ))}
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Skeleton variant="text" width={90} height={14} />
              <Skeleton variant="rounded" width={50} height={30} />
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ height: "1px", background: "#e0e0e0", mx: 2 }} />

        {/* Advisor Settings Section */}
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width={150} height={22} sx={{ mb: 1.5 }} />

          {/* Advisor Table Header */}
          <Box
            display="grid"
            gridTemplateColumns="1.2fr 3fr 0.8fr 2fr"
            gap={1}
            sx={{ pb: 1, borderBottom: "1px solid #e0e0e0", mb: 1 }}
          >
            {advisorColWidths.map((w, i) => (
              <Skeleton key={i} variant="text" width={`${w}%`} height={14} />
            ))}
          </Box>

          {/* Advisor Rows */}
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              display="grid"
              gridTemplateColumns="1.2fr 3fr 0.8fr 2fr"
              gap={1}
              sx={{ py: 1.2, borderBottom: "1px solid #f0f0f0" }}
            >
              <Skeleton
                variant="text"
                width={`${60 + (i % 3) * 15}%`}
                height={14}
              />
              <Skeleton
                variant="text"
                width={`${70 + (i % 2) * 20}%`}
                height={14}
              />
              <Skeleton variant="text" width="40%" height={14} />
              <Skeleton
                variant="text"
                width={`${65 + (i % 2) * 15}%`}
                height={14}
              />
            </Box>
          ))}
        </Box>
      </Paper>
    </>
  );
};

export default BrandConfigOverviewSkeleton;